import React, { useState } from 'react';
import { Users, Calendar, AlertTriangle } from 'lucide-react';
import FloorPlanHeatmap from './shared/FloorPlanHeatmap';

const SpaceUtilization: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'utilization' | 'meeting' | 'neighborhood'>('utilization');
  const [currentHour, setCurrentHour] = useState(13);
  const [selectedNeighborhood, setSelectedNeighborhood] = useState<string | null>(null);

  const sampleZones = [
    { id: 'north-wing', name: 'North Wing', x: 50, y: 50, width: 200, height: 150, occupancy: [0,8,24,38,42,48,46,42,38,28,18], capacity: 50 },
    { id: 'south-wing', name: 'South Wing', x: 300, y: 50, width: 200, height: 150, occupancy: [0,12,18,28,32,36,34,30,24,18,12], capacity: 40 },
    { id: 'conf-a', name: 'Conf A', x: 550, y: 50, width: 120, height: 70, occupancy: [0,0,8,12,12,10,8,6,0,0,0], capacity: 12 },
    { id: 'conf-b', name: 'Conf B', x: 550, y: 130, width: 120, height: 70, occupancy: [0,0,0,0,8,10,8,6,0,0,0], capacity: 12 },
    { id: 'open-area', name: 'Open Area', x: 50, y: 250, width: 300, height: 200, occupancy: [0,18,42,62,68,72,68,58,42,32,20], capacity: 85 },
    { id: 'break-room', name: 'Break', x: 400, y: 250, width: 140, height: 90, occupancy: [0,6,12,18,24,28,22,16,10,6,4], capacity: 30 },
    { id: 'focus-pods', name: 'Focus', x: 400, y: 360, width: 140, height: 90, occupancy: [0,8,12,14,14,14,12,10,6,4,2], capacity: 16 },
    { id: 'board-room', name: 'Board', x: 590, y: 250, width: 80, height: 200, occupancy: [0,0,0,16,16,14,0,0,0,0,0], capacity: 20 }
  ];

  const peakDayData = [
    { day: 'Mon', hours: [0,12,28,42,48,52,48,44,36,28,16], color: '#0A9B9B66' },
    { day: 'Tue', hours: [0,18,38,56,64,68,64,58,48,34,20], color: '#0A9B9B88' },
    { day: 'Wed', hours: [0,22,42,62,72,76,72,64,52,38,22], color: '#0A9B9B' },
    { day: 'Thu', hours: [0,20,36,54,62,66,62,56,46,32,18], color: '#0A9B9B88' },
    { day: 'Fri', hours: [0,14,24,36,42,46,42,36,28,20,12], color: '#0A9B9B66' }
  ];

  const meetingRooms = [
    {
      id: 'conf-a',
      name: 'Conference Room A',
      capacity: 12,
      bookedHours: 28,
      occupiedHours: 18,
      ghostMeetings: 7,
      wastePercent: 36,
      avgAttendees: 4.2
    },
    {
      id: 'conf-b',
      name: 'Conference Room B',
      capacity: 12,
      bookedHours: 24,
      occupiedHours: 6,
      ghostMeetings: 12,
      wastePercent: 75,
      avgAttendees: 2.1
    },
    {
      id: 'board-room',
      name: 'Board Room',
      capacity: 20,
      bookedHours: 16,
      occupiedHours: 14,
      ghostMeetings: 1,
      wastePercent: 13,
      avgAttendees: 8.4
    },
    {
      id: 'focus-pods',
      name: 'Focus Pods (4x)',
      capacity: 16,
      bookedHours: 42,
      occupiedHours: 36,
      ghostMeetings: 4,
      wastePercent: 14,
      avgAttendees: 1.8
    }
  ];

  const bookedZones = activeTab === 'meeting' && currentHour >= 10 && currentHour <= 14
    ? ['conf-a', 'conf-b', 'board-room']
    : [];

  return (
    <div className="p-6 space-y-6" style={{ maxWidth: '1600px', margin: '0 auto' }}>
      <div className="mb-6">
        <h2 className="text-2xl font-bold mb-2" style={{ color: 'var(--sca-ink)' }}>
          Space Utilization
        </h2>
        <p style={{ color: 'var(--sca-muted)' }}>
          Floor-plan occupancy intelligence. Sample building data, illustrative
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b" style={{ borderColor: 'var(--sca-border)' }}>
        <button
          onClick={() => setActiveTab('utilization')}
          className="px-4 py-2 font-medium text-sm transition-all"
          style={{
            color: activeTab === 'utilization' ? 'var(--sca-teal)' : 'var(--sca-muted)',
            background: 'transparent',
            border: 'none',
            borderBottom: activeTab === 'utilization' ? '2px solid var(--sca-teal)' : 'none'
          }}
        >
          Utilization
        </button>
        <button
          onClick={() => setActiveTab('meeting')}
          className="px-4 py-2 font-medium text-sm transition-all"
          style={{
            color: activeTab === 'meeting' ? 'var(--sca-teal)' : 'var(--sca-muted)',
            background: 'transparent',
            border: 'none',
            borderBottom: activeTab === 'meeting' ? '2px solid var(--sca-teal)' : 'none'
          }}
        >
          Meeting Intelligence
        </button>
        <button
          onClick={() => setActiveTab('neighborhood' as any)}
          className="px-4 py-2 font-medium text-sm transition-all"
          style={{
            color: activeTab === 'neighborhood' ? 'var(--sca-teal)' : 'var(--sca-muted)',
            background: 'transparent',
            border: 'none',
            borderBottom: activeTab === 'neighborhood' ? '2px solid var(--sca-teal)' : 'none'
          }}
        >
          Neighborhood Scorecard
        </button>
      </div>

      {/* Utilization Tab */}
      {activeTab === 'utilization' && (
        <div className="space-y-6">
          {/* Hero: Floor Plan Heatmap */}
          <div className="rounded-lg border p-6" style={{ backgroundColor: 'var(--sca-card)', borderColor: 'var(--sca-border)' }}>
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-xl font-semibold mb-1" style={{ color: 'var(--sca-ink)' }}>
                  Floor 3 Occupancy: Wednesday
                </h3>
                <p className="text-sm" style={{ color: 'var(--sca-muted)' }}>
                  Scrub time to see presence patterns. Floor sensor presence, not badge entry.
                </p>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold" style={{ color: 'var(--sca-ink)' }}>
                  {sampleZones.reduce((sum, z) => sum + (z.occupancy[currentHour - 8] || 0), 0)} present
                </div>
                <div className="text-sm" style={{ color: 'var(--sca-muted)' }}>
                  of 263 capacity
                </div>
              </div>
            </div>

            <FloorPlanHeatmap
              zones={sampleZones}
              currentHour={currentHour}
              onHourChange={setCurrentHour}
              showScrubber={true}
              width={700}
              height={460}
              mode="heatmap"
            />

            <div className="mt-4 flex items-center gap-6 text-xs" style={{ color: 'var(--sca-muted)' }}>
              <div className="flex items-center gap-2">
                <div style={{ width: 16, height: 16, backgroundColor: '#F9F7F2', border: '1px solid #e8e4dc' }}></div>
                <span>Empty</span>
              </div>
              <div className="flex items-center gap-2">
                <div style={{ width: 16, height: 16, backgroundColor: '#0A9B9B22' }}></div>
                <span>1-25%</span>
              </div>
              <div className="flex items-center gap-2">
                <div style={{ width: 16, height: 16, backgroundColor: '#0A9B9B44' }}></div>
                <span>25-50%</span>
              </div>
              <div className="flex items-center gap-2">
                <div style={{ width: 16, height: 16, backgroundColor: '#0A9B9B88' }}></div>
                <span>50-75%</span>
              </div>
              <div className="flex items-center gap-2">
                <div style={{ width: 16, height: 16, backgroundColor: '#0A9B9B' }}></div>
                <span>75-100%</span>
              </div>
            </div>
          </div>

          {/* Peak Day Curves */}
          <div className="rounded-lg border p-6" style={{ backgroundColor: 'var(--sca-card)', borderColor: 'var(--sca-border)' }}>
            <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--sca-ink)' }}>
              Peak-Day Occupancy Curves
            </h3>
            <p className="text-sm mb-4" style={{ color: 'var(--sca-muted)' }}>
              Mon-Fri presence patterns show Wed peak (72% at 1 PM). Optimize for peaks, not averages.
            </p>

            <div className="grid grid-cols-5 gap-3">
              {peakDayData.map((day) => (
                <div key={day.day}>
                  <div className="text-xs font-medium mb-2 text-center" style={{ color: 'var(--sca-ink)' }}>
                    {day.day}
                  </div>
                  <svg width="100%" height="120" viewBox="0 0 100 120">
                    <line x1="0" y1="100" x2="100" y2="100" stroke="#e8e4dc" strokeWidth="1" />
                    <line x1="0" y1="20" x2="100" y2="20" stroke="#e8e4dc" strokeWidth="1" strokeDasharray="2,2" />

                    <polyline
                      points={day.hours.map((val, i) => `${i * 10},${100 - val}`).join(' ')}
                      fill="none"
                      stroke={day.color}
                      strokeWidth="2"
                    />

                    {day.hours.map((val, i) => (
                      <circle
                        key={i}
                        cx={i * 10}
                        cy={100 - val}
                        r="2"
                        fill={day.color}
                      />
                    ))}
                  </svg>
                  <div className="text-xs text-center mt-1" style={{ color: 'var(--sca-muted)' }}>
                    Peak: {Math.max(...day.hours)}%
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 p-3 rounded-lg" style={{ backgroundColor: 'var(--sca-hover)' }}>
              <div className="text-xs font-semibold mb-1" style={{ color: 'var(--sca-ink)' }}>
                Constraint Analysis
              </div>
              <div className="text-sm" style={{ color: 'var(--sca-muted)' }}>
                Wed 10AM-2PM near capacity. Focus pods saturated (88%). Large rooms idle (Board: 20% avg). 
                Inventory mix issue, not sqft shortage.
              </div>
            </div>
          </div>

          {/* Zone Performance */}
          <div className="rounded-lg border p-6" style={{ backgroundColor: 'var(--sca-card)', borderColor: 'var(--sca-border)' }}>
            <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--sca-ink)' }}>
              Zone Performance (8-Week)
            </h3>

            <div className="space-y-3">
              {[
                { zone: 'Open Area', util: 68, peak: 85, vacancy: 4, sqft: 3200 },
                { zone: 'North Wing', util: 62, peak: 78, vacancy: 8, sqft: 2400 },
                { zone: 'South Wing', util: 54, peak: 72, vacancy: 12, sqft: 2000 },
                { zone: 'Focus Pods', util: 72, peak: 88, vacancy: 2, sqft: 480 },
                { zone: 'Board Room', util: 22, peak: 65, vacancy: 28, sqft: 640 }
              ].map((zone, idx) => (
                <div
                  key={idx}
                  className="p-3 rounded-lg"
                  style={{ backgroundColor: 'var(--sca-hover)', border: '1px solid var(--sca-border)' }}
                >
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="font-medium" style={{ color: 'var(--sca-ink)' }}>{zone.zone}</div>
                      <div className="text-xs" style={{ color: 'var(--sca-muted)' }}>{zone.sqft} sq ft</div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold" style={{
                        color: zone.util < 40 ? '#c33' : zone.util < 55 ? '#8a7355' : '#0A9B9B'
                      }}>
                        {zone.util}%
                      </div>
                      <div className="text-xs" style={{ color: 'var(--sca-muted)' }}>avg util</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4 text-xs">
                    <div>
                      <div style={{ color: 'var(--sca-muted)' }}>Peak</div>
                      <div className="font-semibold" style={{ color: 'var(--sca-ink)' }}>{zone.peak}%</div>
                    </div>
                    <div>
                      <div style={{ color: 'var(--sca-muted)' }}>Vacancy</div>
                      <div className="font-semibold" style={{ color: 'var(--sca-ink)' }}>{zone.vacancy} wk</div>
                    </div>
                    <div>
                      <div style={{ color: 'var(--sca-muted)' }}>Peak:Avg</div>
                      <div className="font-semibold" style={{ color: 'var(--sca-ink)' }}>
                        {(zone.peak / zone.util).toFixed(1)}x
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Meeting Intelligence Tab */}
      {activeTab === 'meeting' && (
        <div className="space-y-6">
          {/* Summary Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="p-4 rounded-lg" style={{ backgroundColor: '#fee', border: '1px solid #fcc' }}>
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle size={18} style={{ color: '#c33' }} />
                <span className="text-sm" style={{ color: '#c33' }}>Ghost Meetings</span>
              </div>
              <div className="text-3xl font-bold" style={{ color: '#c33' }}>24</div>
              <div className="text-xs" style={{ color: '#c33' }}>this week (booked but empty)</div>
            </div>

            <div className="p-4 rounded-lg" style={{ backgroundColor: '#fef9e7', border: '1px solid #e8dfd0' }}>
              <div className="flex items-center gap-2 mb-2">
                <Calendar size={18} style={{ color: '#8a7355' }} />
                <span className="text-sm" style={{ color: '#8a7355' }}>Wasted Hours</span>
              </div>
              <div className="text-3xl font-bold" style={{ color: '#8a7355' }}>110h</div>
              <div className="text-xs" style={{ color: '#8a7355' }}>booked with no presence</div>
            </div>

            <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--sca-badge-bg)', border: '1px solid var(--sca-teal-muted)' }}>
              <div className="flex items-center gap-2 mb-2">
                <Users size={18} style={{ color: 'var(--sca-teal)' }} />
                <span className="text-sm" style={{ color: 'var(--sca-teal)' }}>Avg Attendees</span>
              </div>
              <div className="text-3xl font-bold" style={{ color: 'var(--sca-teal)' }}>4.1</div>
              <div className="text-xs" style={{ color: 'var(--sca-teal)' }}>actual vs 10 capacity avg</div>
            </div>

            <div className="p-4 rounded-lg" style={{ backgroundColor: '#fef9e7', border: '1px solid #e8dfd0' }}>
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle size={18} style={{ color: '#8a7355' }} />
                <span className="text-sm" style={{ color: '#8a7355' }}>Oversized Rate</span>
              </div>
              <div className="text-3xl font-bold" style={{ color: '#8a7355' }}>58%</div>
              <div className="text-xs" style={{ color: '#8a7355' }}>room too large for group</div>
            </div>
          </div>

          {/* Booked vs Present Plan */}
          <div className="rounded-lg border p-6" style={{ backgroundColor: 'var(--sca-card)', borderColor: 'var(--sca-border)' }}>
            <div className="mb-4">
              <h3 className="text-xl font-semibold mb-1" style={{ color: 'var(--sca-ink)' }}>
                Booked vs Present: Floor 3
              </h3>
              <p className="text-sm" style={{ color: 'var(--sca-muted)' }}>
                Calendar said full. Floor said vacant. Scrub time to see ghost meetings in place.
              </p>
            </div>

            <FloorPlanHeatmap
              zones={sampleZones}
              currentHour={currentHour}
              onHourChange={setCurrentHour}
              showScrubber={true}
              width={700}
              height={460}
              mode="availability"
              bookedZones={bookedZones}
            />

            <div className="mt-4 flex items-center gap-6 text-xs" style={{ color: 'var(--sca-muted)' }}>
              <div className="flex items-center gap-2">
                <div style={{ width: 16, height: 16, backgroundColor: '#fee', border: '1px solid #fcc' }}></div>
                <span>Booked but empty (ghost)</span>
              </div>
              <div className="flex items-center gap-2">
                <div style={{ width: 16, height: 16, backgroundColor: '#0A9B9B33', border: '1px solid #0A9B9B' }}></div>
                <span>Presence detected</span>
              </div>
              <div className="flex items-center gap-2">
                <div style={{ width: 16, height: 16, backgroundColor: '#F9F7F2', border: '1px solid #e8e4dc' }}></div>
                <span>Available</span>
              </div>
            </div>
          </div>

          {/* Book-to-Presence Funnel */}
          <div className="rounded-lg border p-6" style={{ backgroundColor: 'var(--sca-card)', borderColor: 'var(--sca-border)' }}>
            <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--sca-ink)' }}>
              Book-to-Presence Funnel (This Week)
            </h3>

            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm" style={{ color: 'var(--sca-muted)' }}>Booked Meetings</span>
                <span className="text-lg font-bold" style={{ color: 'var(--sca-ink)' }}>156</span>
              </div>
              <div className="w-full h-8 rounded" style={{ backgroundColor: '#0A9B9B' }}></div>
            </div>

            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm" style={{ color: 'var(--sca-muted)' }}>Arrived (Presence)</span>
                <span className="text-lg font-bold" style={{ color: 'var(--sca-ink)' }}>132</span>
              </div>
              <div className="w-5/6 h-8 rounded" style={{ backgroundColor: '#0A9B9B88' }}></div>
            </div>

            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm" style={{ color: 'var(--sca-muted)' }}>Meaningful Dwell (&gt;5min)</span>
                <span className="text-lg font-bold" style={{ color: 'var(--sca-ink)' }}>118</span>
              </div>
              <div className="w-3/4 h-8 rounded" style={{ backgroundColor: '#0A9B9B66' }}></div>
            </div>

            <div className="p-4 rounded-lg" style={{ backgroundColor: '#fee', border: '1px solid #fcc' }}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold" style={{ color: '#c33' }}>Ghosted (No Presence)</span>
                <span className="text-lg font-bold" style={{ color: '#c33' }}>24 meetings</span>
              </div>
            </div>
          </div>

          {/* Room Rightsizing */}
          <div className="rounded-lg border p-6" style={{ backgroundColor: 'var(--sca-card)', borderColor: 'var(--sca-border)' }}>
            <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--sca-ink)' }}>
              Room Rightsizing Analysis
            </h3>
            <p className="text-sm mb-4" style={{ color: 'var(--sca-muted)' }}>
              Actual attendance vs room capacity. Meetings using &lt;50% of capacity should move to smaller rooms.
            </p>

            <div className="space-y-3">
              {meetingRooms.map((room) => (
                <div
                  key={room.id}
                  className="p-4 rounded-lg border"
                  style={{
                    backgroundColor: room.wastePercent > 30 ? '#fef9e7' : 'var(--sca-card)',
                    borderColor: room.wastePercent > 30 ? '#e8dfd0' : 'var(--sca-border)'
                  }}
                >
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <div className="font-semibold" style={{ color: 'var(--sca-ink)' }}>{room.name}</div>
                      <div className="text-xs" style={{ color: 'var(--sca-muted)' }}>
                        Capacity: {room.capacity} • Avg attendees: {room.avgAttendees}
                      </div>
                    </div>
                    {room.wastePercent > 30 && (
                      <span className="text-xs px-2 py-1 rounded font-medium" style={{
                        backgroundColor: '#c33',
                        color: 'white'
                      }}>
                        {room.wastePercent}% waste
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <div className="text-xs" style={{ color: 'var(--sca-muted)' }}>Booked</div>
                      <div className="font-semibold" style={{ color: 'var(--sca-ink)' }}>{room.bookedHours}h</div>
                    </div>
                    <div>
                      <div className="text-xs" style={{ color: 'var(--sca-muted)' }}>Occupied</div>
                      <div className="font-semibold" style={{ color: '#0A9B9B' }}>{room.occupiedHours}h</div>
                    </div>
                    <div>
                      <div className="text-xs" style={{ color: 'var(--sca-muted)' }}>Ghosts</div>
                      <div className="font-semibold" style={{ color: '#c33' }}>{room.ghostMeetings}</div>
                    </div>
                  </div>

                  {room.wastePercent > 30 && (
                    <div className="mt-3 text-xs p-2 rounded" style={{ backgroundColor: 'white', color: '#8a7355' }}>
                      <strong>Suggestion:</strong> {
                        room.capacity >= 12 
                          ? 'Split into 2 huddle rooms (6-person) + phone booths'
                          : 'Repurpose as focus space or smaller huddle'
                      }
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Neighborhood Scorecard */}
      {activeTab === 'neighborhood' && (
        <div className="space-y-6">
          <div className="rounded-lg border p-6" style={{ backgroundColor: 'var(--sca-card)', borderColor: 'var(--sca-border)' }}>
            <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--sca-ink)' }}>
              Neighborhood Scorecard by Floor
            </h3>
            <p className="text-sm mb-4" style={{ color: 'var(--sca-muted)' }}>
              Per-floor scores for peak availability, meeting supply, amenity reach, and vacancy. Click row for merge/split/reprogram recommendations.
            </p>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--sca-border)' }}>
                    <th className="text-left py-3 px-4" style={{ color: 'var(--sca-ink)', fontWeight: '600' }}>Floor</th>
                    <th className="text-right py-3 px-4" style={{ color: 'var(--sca-ink)', fontWeight: '600' }}>Peak Seat Availability</th>
                    <th className="text-right py-3 px-4" style={{ color: 'var(--sca-ink)', fontWeight: '600' }}>Meeting Supply</th>
                    <th className="text-right py-3 px-4" style={{ color: 'var(--sca-ink)', fontWeight: '600' }}>Amenity Reach</th>
                    <th className="text-right py-3 px-4" style={{ color: 'var(--sca-ink)', fontWeight: '600' }}>Vacancy Streak</th>
                    <th className="text-right py-3 px-4" style={{ color: 'var(--sca-ink)', fontWeight: '600' }}>Score</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    {
                      id: 'floor-3',
                      floor: 'Floor 3',
                      peakAvailability: 42,
                      meetingSupply: 'Tight',
                      meetingScore: 35,
                      amenityReach: 'High',
                      amenityScore: 82,
                      vacancyStreak: 2,
                      overallScore: 68
                    },
                    {
                      id: 'floor-2',
                      floor: 'Floor 2',
                      peakAvailability: 28,
                      meetingSupply: 'Constrained',
                      meetingScore: 22,
                      amenityReach: 'Medium',
                      amenityScore: 58,
                      vacancyStreak: 8,
                      overallScore: 48
                    },
                    {
                      id: 'floor-1',
                      floor: 'Floor 1',
                      peakAvailability: 68,
                      meetingSupply: 'Adequate',
                      meetingScore: 72,
                      amenityReach: 'High',
                      amenityScore: 88,
                      vacancyStreak: 0,
                      overallScore: 82
                    },
                    {
                      id: 'floor-4',
                      floor: 'Floor 4',
                      peakAvailability: 18,
                      meetingSupply: 'Undersupplied',
                      meetingScore: 12,
                      amenityReach: 'Low',
                      amenityScore: 32,
                      vacancyStreak: 15,
                      overallScore: 32
                    }
                  ].map((neighborhood) => {
                    const getScoreColor = (score: number) => {
                      if (score >= 70) return '#0A9B9B';
                      if (score >= 50) return '#8a7355';
                      return '#c33';
                    };

                    return (
                      <tr
                        key={neighborhood.id}
                        onClick={() => setSelectedNeighborhood(
                          selectedNeighborhood === neighborhood.id ? null : neighborhood.id
                        )}
                        className="cursor-pointer transition-colors"
                        style={{
                          borderBottom: '1px solid var(--sca-border)',
                          backgroundColor: selectedNeighborhood === neighborhood.id ? 'var(--sca-hover)' : 'transparent'
                        }}
                        onMouseEnter={(e) => {
                          if (selectedNeighborhood !== neighborhood.id) {
                            e.currentTarget.style.backgroundColor = 'var(--sca-hover)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (selectedNeighborhood !== neighborhood.id) {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }
                        }}
                      >
                        <td className="py-3 px-4 font-medium" style={{ color: 'var(--sca-ink)' }}>
                          {neighborhood.floor}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <span className="font-semibold" style={{ color: getScoreColor(neighborhood.peakAvailability * 2) }}>
                            {neighborhood.peakAvailability}%
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <span style={{ color: getScoreColor(neighborhood.meetingScore) }}>
                            {neighborhood.meetingSupply}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <span style={{ color: getScoreColor(neighborhood.amenityScore) }}>
                            {neighborhood.amenityReach}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <span className="font-semibold" style={{ 
                            color: neighborhood.vacancyStreak > 10 ? '#c33' : neighborhood.vacancyStreak > 5 ? '#8a7355' : '#0A9B9B'
                          }}>
                            {neighborhood.vacancyStreak} wk
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <span className="font-bold text-lg" style={{ color: getScoreColor(neighborhood.overallScore) }}>
                            {neighborhood.overallScore}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Planner Recommendations */}
          {selectedNeighborhood && (
            <div className="rounded-lg border p-6" style={{ backgroundColor: '#fef9e7', borderColor: '#e8dfd0' }}>
              <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--sca-ink)' }}>
                Planner Recommendations
              </h3>

              {selectedNeighborhood === 'floor-4' && (
                <div className="space-y-4">
                  <div className="p-4 rounded-lg" style={{ backgroundColor: 'white', border: '1px solid var(--sca-border)' }}>
                    <div className="text-xs font-semibold mb-2" style={{ color: '#c33' }}>MERGE OPPORTUNITY</div>
                    <h4 className="font-semibold mb-2" style={{ color: 'var(--sca-ink)' }}>
                      Consolidate Floor 4 into Floors 1-3
                    </h4>
                    <p className="text-sm mb-3" style={{ color: 'var(--sca-muted)' }}>
                      Floor 4 shows 18% peak availability, undersupplied meetings, low amenity reach, 15-week vacancy streak. Score: 32.
                    </p>
                    <div className="text-sm" style={{ color: 'var(--sca-ink)' }}>
                      <strong>Action:</strong> Merge Floor 4 occupants to Floor 1 (68% peak availability, adequate meeting supply). 
                      Reclaim 7,200 sq ft. Relocate 3 meeting rooms to Floor 2 to relieve constraint.
                    </div>
                  </div>

                  <div className="p-4 rounded-lg" style={{ backgroundColor: 'white', border: '1px solid var(--sca-border)' }}>
                    <div className="text-xs font-semibold mb-2" style={{ color: '#8a7355' }}>IMPACT ANALYSIS</div>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <div className="text-xs" style={{ color: 'var(--sca-muted)' }}>Reclaim</div>
                        <div className="font-semibold" style={{ color: 'var(--sca-ink)' }}>7,200 sq ft</div>
                      </div>
                      <div>
                        <div className="text-xs" style={{ color: 'var(--sca-muted)' }}>Floor 1 New Peak</div>
                        <div className="font-semibold" style={{ color: '#0A9B9B' }}>82% (safe)</div>
                      </div>
                      <div>
                        <div className="text-xs" style={{ color: 'var(--sca-muted)' }}>Meeting Pressure</div>
                        <div className="font-semibold" style={{ color: '#0A9B9B' }}>Resolved</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {selectedNeighborhood === 'floor-2' && (
                <div className="space-y-4">
                  <div className="p-4 rounded-lg" style={{ backgroundColor: 'white', border: '1px solid var(--sca-border)' }}>
                    <div className="text-xs font-semibold mb-2" style={{ color: '#8a7355' }}>REPROGRAM OPPORTUNITY</div>
                    <h4 className="font-semibold mb-2" style={{ color: 'var(--sca-ink)' }}>
                      Add Meeting Rooms to Floor 2
                    </h4>
                    <p className="text-sm mb-3" style={{ color: 'var(--sca-muted)' }}>
                      Floor 2 shows constrained meeting supply (score: 22) despite 28% peak seat availability.
                    </p>
                    <div className="text-sm" style={{ color: 'var(--sca-ink)' }}>
                      <strong>Action:</strong> Convert 4 underused open desks to 2 huddle rooms (6-person). 
                      Improves meeting score from 22 to 58. Cost: $24K (build-out).
                    </div>
                  </div>
                </div>
              )}

              {selectedNeighborhood === 'floor-3' && (
                <div className="space-y-4">
                  <div className="p-4 rounded-lg" style={{ backgroundColor: 'white', border: '1px solid var(--sca-border)' }}>
                    <div className="text-xs font-semibold mb-2" style={{ color: '#0A9B9B' }}>OPTIMIZE</div>
                    <h4 className="font-semibold mb-2" style={{ color: 'var(--sca-ink)' }}>
                      Floor 3 Performing Well
                    </h4>
                    <p className="text-sm mb-3" style={{ color: 'var(--sca-muted)' }}>
                      Score: 68. Peak availability 42%, tight but manageable meeting supply, high amenity reach.
                    </p>
                    <div className="text-sm" style={{ color: 'var(--sca-ink)' }}>
                      <strong>Action:</strong> Add 8 focus pods to address meeting tightness without full room build-out. Cost: $16K.
                    </div>
                  </div>
                </div>
              )}

              {selectedNeighborhood === 'floor-1' && (
                <div className="space-y-4">
                  <div className="p-4 rounded-lg" style={{ backgroundColor: 'white', border: '1px solid var(--sca-border)' }}>
                    <div className="text-xs font-semibold mb-2" style={{ color: '#0A9B9B' }}>STRONG PERFORMER</div>
                    <h4 className="font-semibold mb-2" style={{ color: 'var(--sca-ink)' }}>
                      Floor 1 Ready for Density
                    </h4>
                    <p className="text-sm mb-3" style={{ color: 'var(--sca-muted)' }}>
                      Score: 82. High peak availability (68%), adequate meeting supply, high amenity reach, no vacancy.
                    </p>
                    <div className="text-sm" style={{ color: 'var(--sca-ink)' }}>
                      <strong>Action:</strong> Target for Floor 4 consolidation. Can absorb 32 occupants while maintaining 82% peak (safe margin).
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SpaceUtilization;
